import React, { useState } from "react";
import qs from "qs";
import { HeroHomeImagesFragment, HeroHomeFragment } from "../../types/graphql";
import {
  HeroBifurcatedBase,
  HeroBifurcatedContent,
  Toggle,
  HeroBifurcatedCTAs,
  StyledCTA,
  Certifications,
  ToggleButton,
  QuoteCTABackground,
  QuoteCTATitle,
  HeroCTAs,
  FormOverlay,
  FormContainer,
  FormContent,
  ToggleLabel,
} from "./hero-bifurcated.styled";
import UploadFilePanel, {
  IUploadFilePanelProps,
} from "../upload-file-panel/upload-file-panel";
import { LoadingPanelHome } from "../upload-file-panel/upload-file-panel.styled";
import * as FA from "../../styles/fontawesome";
import { useThemeContext } from "../../utils/theme-context";
import { identify, track } from "../../utils/analytics";
import {
  FormMicroComponent,
  closeFormMicroComponent,
  singleFormProps,
} from "../customer-quote-request/customer-quote-request";
import {
  FormProps,
  SubmittedFormData,
} from "../customer-quote-request/form-stepper-component";
import { contactFormId } from "../../utils/constants";
import { useSubmitContactFormMutation } from "../../types/queries";
import GeoMessage from "../geo-message/geo-message";
import { IconShieldCheck, IconCertificate } from "./hero-bifurcated-icons";

interface IHeroBifurcatedProps {
  heroHome: HeroHomeFragment;
  heroHomeImages: HeroHomeImagesFragment;
}

type ITabState = "info" | "form" | "formSuccess";

const HeroBifurcated = (props: IHeroBifurcatedProps) => {
  const heroHome = props.heroHome.primary;
  const [formState, setFormState] = useState<ITabState>("info");
  const thirdTabLeadFormText = "Talk To Our Team";
  const leadFormEvent = "Lead Form Clicked";
  const customerRfqDivId = "customerRFQForm";
  const [formEmail, setFormEmail] = useState<string | undefined>();
  const { currentMode, setCurrentMode, currentTransitionPercent } =
    useThemeContext();

  const parsedUrl = heroHome?.quote_cta_link?.url
    ? qs.parse(heroHome?.quote_cta_link?.url)
    : { process: "" };

  const queryProcess = Object.prototype.hasOwnProperty.call(
    parsedUrl,
    "process"
  )
    ? parsedUrl.process
    : "";

  const ctaButton = (
    <div>
      <i css={FA.CloudArrowUpSolid} />
      &nbsp;&nbsp;{heroHome.quote_cta_text || "Start Your New Instant Quote"}
    </div>
  );

  const fileUploadContent = (
    <QuoteCTABackground>
      <QuoteCTATitle>
        Get instant pricing, project lead times, and DFM feedback.
      </QuoteCTATitle>
    </QuoteCTABackground>
  );

  const LoadingSkeletonCard = () => <LoadingPanelHome />;

  const uploadProps = {
    panelStyle: "blueSolid",
    processInitialism: queryProcess,
    segmentTrackPageSection: "home-hero",
    children: fileUploadContent,
    showSupportedFileTypes: true,
    instantQuoteButtonContent: ctaButton,
    showSecureBlurb: true,
    showPatentNotice: true,
    panelScale: "large",
    Fallback: LoadingSkeletonCard,
  } as IUploadFilePanelProps;

  const Certs = () => {
    return (
      <Certifications
        isDarkMode={currentMode}
        transitionPercent={currentTransitionPercent}
      >
        <li>
          <IconShieldCheck />
          <span>ITAR Registered</span>
        </li>
        <li>
          <IconCertificate />
          <span>
            ISO 9001:2015, ISO 13485:2016, AS9100D and IATF 16949:2016 Certified
          </span>
        </li>
      </Certifications>
    );
  };

  const [submitContactForm] = useSubmitContactFormMutation();

  const formProps: FormProps = {
    config: {
      ...singleFormProps.config,
      singleForm: {
        id: contactFormId,
        onSubmit: async (formData) => {
          const submissionResponse = await submitContactForm({
            variables: {
              input: {
                formId: contactFormId,
                leadSource: "Full Life Cycle Form",
                formFieldsResponses: formData,
              },
            },
          });
          if (
            submissionResponse.data?.submitContactForm?.status === "success"
          ) {
            setFormState("formSuccess");
            identifyFromForm(formData);
            const emailAddress = formData.find((data) => data.key === "email");
            setFormEmail(emailAddress?.value);
            return true;
          }
          return false;
        },
        onClose: () => {
          setFormState("info");
        },
      },
    },
  };

  const identifyFromForm = (formData: SubmittedFormData[]) => {
    const emailAddress = formData.find((data) => data.key === "email");
    const company = formData.find((data) => data.key === "company");
    const firstName = formData.find((data) => data.key === "firstName");
    const lastName = formData.find((data) => data.key === "lastName");
    const jobTitle = formData.find((data) => data.key === "jobTitle");
    const phone = formData.find((data) => data.key === "phone");
    identify({
      email: emailAddress?.value,
      company_name: company?.value,
      name: `${firstName?.value} ${lastName?.value}`,
      job_title: jobTitle?.value,
      phone: phone?.value,
    });
  };

  return (
    (heroHome && (
      <HeroBifurcatedBase
        isDarkMode={currentMode}
        transitionPercent={currentTransitionPercent}
      >
        <div className="container">
          <HeroBifurcatedContent
            isDarkMode={currentMode}
            transitionPercent={currentTransitionPercent}
          >
            <Toggle>
              <ToggleLabel aria-label="Low Volume Manufacturing">
                Low Volume Manufacturing
              </ToggleLabel>
              <ToggleButton
                onClick={() => {
                  setCurrentMode(!currentMode);
                }}
                isDarkMode={currentMode}
                transitionPercent={currentTransitionPercent}
              >
                <span>Choose between Prototyping and Production</span>
              </ToggleButton>
              <ToggleLabel aria-label="Production">
                High Volume Production
              </ToggleLabel>
            </Toggle>
            {!currentMode ? (
              <>
                <div>
                  <h1>Manufacturing on Demand</h1>
                  <p className="intro">
                    Access a massive menu of manufacturing processes, materials,
                    and finishes. Plus, get program management, DFM consulting,
                    and supply chain management. 
                  </p>
                </div>
                <HeroCTAs>
                  <GeoMessage />
                  <UploadFilePanel {...uploadProps} />
                </HeroCTAs>
              </>
            ) : (
              <>
                <div>
                  <h1>
                    High Volume Production, Procurement and Supply Chain
                    Solutions
                  </h1>
                  <p className="intro">
                    Achieve streamlined manufacturing expertise and savings
                    across numerous use cases with Xometry. Our experienced
                    engineering teams provide unprecedented access to
                    manufacturing capacity and support. 
                  </p>
                </div>
                <HeroCTAs>
                  <h4>Let's Make Manufacturing Easier.</h4>
                  <HeroBifurcatedCTAs>
                    <StyledCTA
                      kind="primary"
                      scale="large"
                      onClick={() => {
                        track(leadFormEvent, {
                          text: thirdTabLeadFormText,
                        });
                        closeFormMicroComponent(customerRfqDivId);
                        setFormState("form");
                      }}
                    >
                      {thirdTabLeadFormText}
                    </StyledCTA>
                  </HeroBifurcatedCTAs>
                </HeroCTAs>
              </>
            )}
          </HeroBifurcatedContent>
          <Certs />
        </div>

        {formState === "form" && (
          <FormOverlay>
            <FormContainer>
              <FormMicroComponent {...formProps} />
            </FormContainer>
          </FormOverlay>
        )}
        {formState === "formSuccess" && (
          <FormOverlay>
            <FormContainer>
              <FormContent>
                <h4>Form Submitted</h4>
                <p>
                  Your Request Form has been submitted! Our team will email you
                  at {formEmail} once your request has been reviewed.
                </p>
                <StyledCTA
                  kind="outline-blue"
                  scale="large"
                  onClick={() => {
                    setFormState("info");
                  }}
                >
                  Continue
                </StyledCTA>
              </FormContent>
            </FormContainer>
          </FormOverlay>
        )}
      </HeroBifurcatedBase>
    )) || <></>
  );
};

export default HeroBifurcated;
